// Import React and necessary hooks
import React, { useState, useEffect, useContext } from 'react';

// Import routing components
import { Link, useHistory } from 'react-router-dom';

// Import translation hook
import { t, useTranslation } from 'react-i18next';

// Import Redux hooks
import { useSelector } from 'react-redux';

// Import modal components
import ReactModal from 'react-modal';
import { useModal } from 'react-modal-hook';

// Import custom modal components
import Login from '../modals/Login'; // Login modal
import Signup from '../modals/Signup'; // Signup modal
import ForgotPassword from '../modals/ForgotPassword'; // Forgot password modal
import InstructorSignup from '../modals/InstructorSignup'; // Instructor signup modal

// Import icons
import { icons } from '../../constants/icons';

// Import utility functions and services
import { printcartData, handleSearchChange, logoutUser } from './navbar/helper'; // Helper functions for navbar
import { debounce, throttle } from '../services/debounce';

// Import context for user and cart management
import { UserContext } from '../context/AuthContext'; // User authentication context
import { CartContext } from '../context/CartContext'; // Cart management context

// Import messages for internationalization
import { ar_messages, messages } from '../../constants/message'; // Arabic and other messages

// Import components for UI
import MenuItem from '../../components/menuItem'; // Menu item component
import LanguageSelector from './LanguageSelector'; // Language selector component
import { Spinner } from '../Utils/helper'; // Spinner component for loading states

// Import helper functions for translations
import getTranslationFromMany from '../../services/helper'; // Function to get translations

// Import icons from react-icons
import { RiDashboardLine } from 'react-icons/ri'; // Dashboard icon

// Component code would go here

function Navbar({ me }) {
    const { t, i18n } = useTranslation();
    const [show, setShow] = useState(false);
    const [sidebar, setSidebar] = useState(false);
    const [showMMCategory, setShowMMCategory] = useState(false);
    const [searchBox, setSearchBox] = useState(false);
    const [searchCourseData, setSearchCourseData] = useState([]);
    const [checkoutPrice, setCheckoutPrice] = useState('');
    const [searchParam, setSearchParam] = useState('');
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [loadingCart, setLoadingCart] = useState(false);

    const { cartCount, setCartCount, cartData, setCartData } = useContext(CartContext);
    const { user, loggedIn, category, notificationCount, notifications, resetNotification } = useContext(UserContext);

    const history = useHistory();

    const getCountryId = () => {
        if (loggedIn && user?.country_id) {
            return user.country_id;
        } else {
            const country_id = JSON.parse(localStorage.getItem('unauthedUserCountry')).id;
            return country_id;
        }
    };

    const debouncedSearchChange = debounce((e) => {
        const currentCountryId = getCountryId();
        handleSearchChange(e, setSearchCourseData, loggedIn, currentCountryId, throttledSetLoadingSearch);
    }, 300);

    // NOTE(aalhendi): throttle loading state to prevent excessive re-renders
    const throttledSetLoadingSearch = throttle((isLoading) => {
        setLoadingSearch(isLoading);
    }, 100);

    let helperProps = {
        cartCount,
        setCartCount,
        setCartData,
        loggedIn,
        setLoadingCart,
        t,
    };

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [show]);

    // Check if the 'show' state is true
    // If 'show' is true, disable scrolling on the body element
    // If 'show' is false, enable scrolling on the body element
    // The effect runs whenever the 'show' state changes

    useEffect(() => {
        if (searchCourseData === null || searchCourseData.length <= 0) {
            setSearchBox(false);
        } else {
            setSearchBox(true);
        }
        if (searchParam.length > 0) {
            setSearchBox(true);
        }
    }, [searchCourseData, searchParam]);

    // Check if searchCourseData is null or has no items
    // If there are no search results, hide the search box
    // If there are search results, show the search box
    // Additionally, check if the search parameter has any input
    // If there is input in the search parameter, show the search box
    // The effect runs whenever searchCourseData or searchParam changes

    function redirect(id) {
        history.push(`/course-details/${id}`);
        setSearchBox(false);
    }
    // Use the history object to navigate to the course details page for the given course ID
    // Close the search box after redirecting

    const [openLoginModal, closeLoginModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal
                isOpen
                ariaHideApp={false} // Prevent screen readers from accessing the app while the modal is open
                className="react_modal" // Custom class for styling the modal
                overlayClassName="modal_overlay" // Custom class for styling the modal overlay
            >
                {/* Button to close the modal */}
                <button
                    type="button"
                    className="close" // Class for styling the close button
                    onClick={() => {
                        // Close the login modal and hide the modal
                        closeLoginModal();
                        setShow(false);
                    }}
                >
                    {/* Close icon */}
                    <img src={icons.icon_close} alt="close" />
                </button>
                <Login forgotOpen={openForgotPasswordModal} loginClose={closeLoginModal} signupOpen={openSignupModal} />
            </ReactModal>
        );
    });
    // Create a modal for the login functionality using the useModal hook
    // Set the state to show the modal when it is opened
    // Return the ReactModal component to be rendered

    const [openSignupModal, closeSignupModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal
                isOpen
                ariaHideApp={false} // Prevent screen readers from accessing the app while the modal is open
                className="react_modal" // Custom class for styling the modal
                overlayClassName="modal_overlay" // Custom class for styling the modal overlay
            >
                {/* Button to close the modal */}
                <button
                    type="button"
                    className="close" // Class for styling the close button
                    onClick={() => {
                        closeSignupModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <Signup closeSignup={closeSignupModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });

    const [openInstructorSignupModal, closeInstructorSignupModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeInstructorSignupModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <InstructorSignup closeSignup={closeInstructorSignupModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });
    const [openForgotPasswordModal, closeForgotPasswordModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeForgotPasswordModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <ForgotPassword closeForgot={closeForgotPasswordModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });

    useEffect(() => {
        let _price = 0;
        if (loggedIn) {
            cartData?.map((item) => {
                if (item?.parent_name) {
                    _price += Number(item?.price);
                } else {
                    if (!item?.is_free) {
                        _price += Number(item?.price);
                    }
                }
            });
            setCheckoutPrice(_price);
        } else {
            cartData?.map((item) => {
                if (!item?.is_free) {
                    _price += Number(item?.price);
                }
            });
            setCheckoutPrice(_price);
        }
    }, [cartData]);

    const handleCheckoutBtn = () => {
        if (loggedIn) {
            history.push('/checkout');
        } else {
            openLoginModal();
            localStorage.setItem('update_cart', true);
        }
    };
    return (
        <>
            <header>
                <div
                    className="logo top_order_1"
                    onClick={() => {
                        history.push('/');
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <a>
                        <img src={icons.dawraty_logo} alt="" />
                        {/* <span>Dawraty</span> */}
                    </a>
                </div>
                <div className="top_order_3">
                    <div className="top_category">
                        {((user?.type === 'student' && loggedIn) || !loggedIn) && (
                            <div className="category_button">
                                <figure>
                                    <img src={icons.icon_category} alt="" />
                                </figure>
                                <span>
                                    <a href="#" className="link_black">
                                        {t('common.categories')}
                                    </a>
                                </span>
                            </div>
                        )}
                        <div className="category_menu">
                            <div className="menu_topic">
                                <div className="menu_item">
                                    <Link to={{ pathname: `/courses/all` }} className="menu_link">
                                        {t('routes.allCourses')}
                                    </Link>
                                </div>
                                {category?.map((cat) => (
                                    <div className="menu_item" key={cat?.id}>
                                        <Link
                                            to={{
                                                pathname: `/courses/${cat.name.split(' ').join('')}`,
                                                state: cat.id,
                                            }}
                                            className={`menu_link`}
                                        >
                                            {t('dynamic', {
                                                en: cat.name,
                                                ar: cat?.translation?.value ? cat?.translation?.value : cat?.name,
                                            })}
                                            {/* Conditional arrow icon */}
                                            {cat.sub_categories && cat.sub_categories.length > 0 && (
                                                <span className="arrow-icon"></span>
                                            )}
                                        </Link>
                                        {cat.sub_categories && cat.sub_categories.length > 0 && (
                                            <div className="sub_menu">
                                                <div className="sub_menu_topic">
                                                    {cat?.sub_categories?.map((sub_cat) => (
                                                        <div className="sub_menu_item" key={sub_cat?.id}>
                                                            <Link
                                                                to={{
                                                                    pathname: `/courses/${cat.name}/${sub_cat.name
                                                                        .split(' ')
                                                                        .join('')}`,
                                                                    state: cat.id,
                                                                    sub_category: sub_cat.id,
                                                                }}
                                                                className="sub_menu_link"
                                                            >
                                                                {t('dynamic', {
                                                                    en: sub_cat.name,
                                                                    ar: sub_cat?.translation?.value
                                                                        ? sub_cat?.translation?.value
                                                                        : sub_cat?.name,
                                                                })}
                                                            </Link>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="search">
                        <form>
                            <div className="search_input" dir={i18n.language == 'ar' ? 'rtl' : 'ltr'}>
                                <div className="search_magnify">
                                    <img src={icons.icon_search} alt="" />
                                </div>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchParam}
                                    placeholder={t('common.search')}
                                    onChange={(e) => {
                                        setSearchParam(e.target.value);
                                        debouncedSearchChange(e);
                                    }}
                                    onFocus={() => {
                                        setSearchBox(true);
                                    }}
                                    onBlur={() => {
                                        (searchParam.length <= 0 || searchCourseData.length === 0) &&
                                            setSearchBox(false);
                                    }}
                                />
                                {searchParam && (
                                    <span
                                        className="reset"
                                        onClick={() => {
                                            setSearchBox(false);
                                            setSearchParam('');
                                            setSearchCourseData([]);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    />
                                )}
                            </div>
                        </form>
                        {searchParam && (
                            <div
                                className="search_dropdown"
                                style={{
                                    display: searchBox ? 'block' : 'none',
                                }}
                                onBlur={() => setSearchBox(false)}
                            >
                                {loadingSearch ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Spinner />
                                    </div>
                                ) : searchCourseData === null || searchCourseData.length === 0 ? (
                                    <div className="no_data plr_20">
                                        {i18n.language === 'ar'
                                            ? ar_messages.NO_RESULTS_FOUND
                                            : messages.NO_RESULTS_FOUND}
                                    </div>
                                ) : (
                                    searchCourseData?.map((item) => (
                                        <div
                                            className="search_row"
                                            key={item?.id}
                                            onClick={() => redirect(item?.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <figure>
                                                <img
                                                    src={process.env.REACT_APP_CONTENT_URL + item?.course_image}
                                                    alt=""
                                                />
                                            </figure>
                                            <div className="search_row_details">
                                                {/* <h4>{item?.name}</h4> */}
                                                <h4>
                                                    {t('dynamic', {
                                                        en: item?.name,
                                                        ar: getTranslationFromMany(
                                                            item.translation,
                                                            'name',
                                                            item?.name
                                                        ),
                                                    })}
                                                </h4>
                                                <span>
                                                    {/* {`${item?.instructor?.first_name} ${item?.instructor?.last_name}`} */}
                                                    {t(
                                                        'courseAuthors.' +
                                                        item?.instructor?.first_name +
                                                        ' ' +
                                                        item?.instructor?.last_name,
                                                        item?.instructor?.first_name + ' ' + item?.instructor?.last_name
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                    <LanguageSelector />
                    {/* language selector */}
                    {!loggedIn && (
                        <>
                            <span
                                // href="#"
                                className="link_black"
                                onClick={openInstructorSignupModal}
                                style={{ cursor: 'pointer' }}
                            >
                                {t('common.becomeAnInstructor')}
                            </span>
                        </>
                    )}
                </div>

                <div className="top_order_2">
                    {/* <!-- Cart Dropdown --> */}
                    {((user?.type === 'student' && loggedIn) || !loggedIn) && (
                        <div className="top_cart">
                            <div
                                className="top_cart_wrapper"
                                id="topCartBUtton"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <figure>
                                    <img src={icons.icon_cart} alt="" />
                                </figure>
                                {cartCount > 0 ? (
                                    cartCount > 9 ? (
                                        <div className="cart_count">9+</div>
                                    ) : (
                                        <div className="cart_count">{cartCount}</div>
                                    )
                                ) : (
                                    ''
                                )}
                            </div>
                            <div
                                className="dropdown-menu cart_dropdown"
                                aria-labelledby="topCartBUtton"
                                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                            >
                                {cartData?.length === 0 ? (
                                    <span className="empty-course">
                                        {t('common.noCourseFound')}{' '}
                                        <div className="keepShpping">{t('checkout.Enroll_Now')}</div>{' '}
                                    </span>
                                ) : (
                                    <>
                                        <div className="dropdown_inner">
                                            {printcartData(cartData, helperProps, history, {
                                                free: t('common.free'),
                                                countryId: getCountryId(),
                                            })}
                                        </div>
                                        <div className="dropdown_checkout">
                                            <strong>
                                                {getCountryId() === 112
                                                    ? t('course.total_price_in_kd', {
                                                        price: checkoutPrice,
                                                    })
                                                    : getCountryId() === 17
                                                        ? t('course.total_price_in_bd', {
                                                            price: checkoutPrice,
                                                        })
                                                        : t('course.total_price_in_jd', {
                                                            price: checkoutPrice,
                                                        })}
                                            </strong>
                                            {/* <Link to="/checkout"> */}
                                            <button
                                                className="btn-block btn_primary big_btn full_btn"
                                                onClick={handleCheckoutBtn}
                                            >
                                                {t('common.checkout')}
                                            </button>
                                            {/* </Link> */}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {loggedIn ? (
                        <>
                            <div className="top_notification">
                                <div
                                    className="notification_bell"
                                    id="topNotifyButton"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    onClick={resetNotification}
                                >
                                    <figure>
                                        <img src={icons.icon_notification} alt="" />
                                    </figure>
                                    {notificationCount > 0 ? (
                                        notificationCount > 9 ? (
                                            <div className="notification_bubble">9+</div>
                                        ) : (
                                            <div className="notification_bubble">{notificationCount}</div>
                                        )
                                    ) : null}
                                </div>
                                <div
                                    className="dropdown-menu notify_dropdown"
                                    aria-labelledby="topNotifyButton"
                                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                                >
                                    <h3>{t('common.notifications')}</h3>
                                    {notifications?.length > 0 ? (
                                        <>
                                            {notifications?.map((item, index) => (
                                                <Link
                                                    to={{
                                                        pathname:
                                                            item?.notification_type.name === 'doubt'
                                                                ? '/chat/' + item.user_id
                                                                : '',
                                                        state: {
                                                            data: item.sender_details,
                                                        },
                                                    }}
                                                    className="notify_row"
                                                    key={index}
                                                >
                                                    <figure>
                                                        <img src={icons.notification_1} alt="" />
                                                    </figure>
                                                    <div className="notify_content">
                                                        <p>{t('notification.' + item.title, item.title)}</p>
                                                        <p>{item.notification_type.name === 'doubt' ? item.first_name + ' ' + item.last_name : ''}</p>
                                                        <span>{item.human_readable_created_at}</span>
                                                    </div>
                                                </Link>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="py-3 no-data">{t('common.noNotifications')}</div>
                                    )}
                                    <Link
                                        to={{
                                            pathname: '/notifications',
                                        }}
                                        className="btn btn-primary mt-3 button_primary"
                                    >
                                        {t('common.showAllNotifications')}
                                    </Link>
                                </div>
                            </div>
                            <div className="top_profile">
                                <div
                                    className="top_profile_img"
                                    id="topProfileButton"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    {user?.img_path !== null ? (
                                        <img src={process.env.REACT_APP_CONTENT_URL + user?.img_path} alt="" />
                                    ) : (
                                        user?.first_name[0]
                                    )}
                                </div>
                                <div
                                    className="dropdown-menu profile_dropdown"
                                    aria-labelledby="topProfileButton"
                                    dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                                >
                                    <div className="pd_top">
                                        <div className="top_profile_img">
                                            {user?.img_path !== null ? (
                                                <img src={process.env.REACT_APP_CONTENT_URL + user?.img_path} alt="" />
                                            ) : (
                                                `${user?.first_name[0]} ${user?.last_name[0]}`
                                            )}
                                        </div>
                                        <div className="pd_ne">
                                            <div className="pd_name">{`${user?.first_name} ${user?.last_name}`}</div>
                                            <div className="pd_email">{user?.email}</div>
                                        </div>
                                    </div>
                                    <div className="pd_bottom">
                                        {user?.type === 'student' && (
                                            <Link to="/student-learning?courses">
                                                <span>
                                                    <RiDashboardLine />
                                                </span>
                                                {t('common.myDashboard')}
                                            </Link>
                                        )}
                                        {user?.type === 'instructor' && (
                                            <Link to="/">
                                                <span>
                                                    <RiDashboardLine />
                                                </span>
                                                {t('common.myDashboard')}
                                            </Link>
                                        )}
                                        <Link to="/profile">
                                            <span>
                                                <img src={icons.icon_user} alt="" />
                                            </span>
                                            {t('common.myProfile')}
                                        </Link>
                                        {user?.type === 'student' && (
                                            <Link to="addresses?all">
                                                <span>
                                                    <img src={icons.icon_address} alt="" />
                                                </span>
                                                {t('common.myAddresses')}
                                            </Link>
                                        )}
                                        {user?.type === 'student' && (
                                            <Link to="student-learning?wishlist">
                                                <span>
                                                    <img src={icons.icon_heart} alt="" />
                                                </span>
                                                {t('common.wishlist')}
                                            </Link>
                                        )}
                                        {user?.type === 'student' && (
                                            <Link to="/my-orders">
                                                <span>
                                                    <img src={icons.icon_order_history} alt="" />
                                                    {/* <AiOutlineShopping /> */}
                                                </span>
                                                {t('common.ordersHistory')}
                                            </Link>
                                        )}
                                        {user?.type === 'instructor' && (
                                            <Link to="/my-orders">
                                                <span>
                                                    <img src={icons.icon_order_history} alt="" />
                                                    {/* <AiOutlineShopping /> */}
                                                </span>
                                                {t('common.recentOrders')}
                                            </Link>
                                        )}
                                        {user?.type === 'instructor' && (
                                            <Link to="/my-courses">
                                                <span>
                                                    <img src={icons.instructor_play} alt="" />
                                                    {/* <IoBookOutline /> */}
                                                </span>
                                                {t('common.myCourses')}
                                            </Link>
                                        )}
                                        {user?.type === 'instructor' && (
                                            <Link to={'/my-students'}>
                                                <span>
                                                    <img src={icons.icon_heart} alt="" />
                                                </span>
                                                {t('common.myStudents')}
                                            </Link>
                                        )}

                                        <a href="/" onClick={logoutUser}>
                                            <span>
                                                <img src={icons.icon_logout} alt="" />
                                            </span>
                                            {t('common.logout')}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <span
                            href="#"
                            className="top_login btn_primary"
                            onClick={openLoginModal}
                            style={{ cursor: 'pointer' }}
                        >
                            {t('common.login')}
                        </span>
                    )}
                    {/* <div className="language_btn">Ar</div> */}

                    <a
                        // href="#"
                        className="hamburger"
                        onClick={() => {
                            setSidebar(true);
                        }}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </a>
                </div>
            </header>
            <div className="mobile_menu" style={{ right: sidebar && '0px' }}>
                <div
                    className="mm_close"
                    onClick={() => {
                        setSidebar(false);
                    }}
                >
                    <img src={icons.icon_close_black} alt="" />
                </div>
                <div className="mm_inner">
                    <a href="#" onClick={openInstructorSignupModal}>
                        {t('common.becomeAnInstructor')}
                    </a>
                    <a
                        onClick={() => {
                            setShowMMCategory(!showMMCategory);
                        }}
                    >
                        Categories
                    </a>
                </div>
                <div className="mm_top_category">
                    {((user?.type === 'student' && loggedIn) || !loggedIn) && (
                        <div
                            className="mm_category_button"
                            onClick={() => {
                                setShowMMCategory(!showMMCategory);
                            }}
                        >
                            <figure>
                                <img src={icons.icon_category} alt="" />
                            </figure>
                            <span>{t('common.categories')}</span>
                        </div>
                    )}
                    {showMMCategory && (
                        <div className="mm_category_menu">
                            <div className="mm_menu_topic">
                                <div className="menu_item">
                                    <Link to={{ pathname: `/courses/all` }} className="menu_link">
                                        {t('routes.allCourses')}
                                    </Link>
                                </div>
                                {category?.map((cat, index) => (
                                    <MenuItem props={cat} key={index}></MenuItem>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Navbar;
